import { useTranslation } from 'react-i18next';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';
import { useMarkerCustomization } from '../hooks/useMarkerCustomization';
import { resolveStatusColors } from '../utils/marker-style';

interface StateBreakdownTooltipProps {
  colors?: {
    green?: number;
    grey?: number;
    gray?: number;
    orange?: number;
    red?: number;
    blue?: number;
  };
  totalCount: number;
}

/**
 * Tooltip showing breakdown of charge point states
 * Displayed on hover over cluster/group markers
 */
export function StateBreakdownTooltip({
  colors,
  totalCount,
}: StateBreakdownTooltipProps) {
  const { t } = useTranslation();
  const uiMode = useUiModeStore((s) => s.uiMode);
  const tooltipStyle = useUiModeStore((s) => s.tooltipStyle);
  const { statusColors } = useMarkerCustomization();

  if (!colors) return null;

  const green = colors.green || 0;
  const grey = (colors.grey || 0) + (colors.gray || 0);
  const red = colors.red || 0;
  const blue = colors.blue || 0;

  // Status ← color-bucket mapping, matching the cluster ring (getConicGradient) and the
  // real API pin colors:
  //   green → Available, grey → Busy, red → Disconnected, blue → Reserved.
  // The API never emits an "orange" bucket (busy points are grey, disconnected ones are
  // red). The ring folds grey + blue into a single grey segment, so the grey/blue tooltip
  // lines share the grey stop while the red line uses the red stop. (The resolved.* names
  // are historical: `disconnected` is the grey stop and `error` the red stop.) Colors come
  // from the same resolver as the ring, so the tooltip reflects any custom marker_color_*
  // values.
  const resolved = resolveStatusColors(statusColors);

  const availableLabel =
    uiMode === 'operator'
      ? t('chargePoint.operatorMode.available')
      : t('common.status.available');

  const states = [
    { count: green, label: availableLabel, color: resolved.available },
    {
      count: grey,
      label: t('common.status.busy'),
      color: resolved.disconnected,
    },
    {
      count: blue,
      label: t('common.status.reserved'),
      color: resolved.disconnected,
    },
    {
      count: red,
      label: t('common.status.disconnected'),
      color: resolved.error,
    },
  ].filter((state) => state.count > 0);

  if (states.length === 0) return null;

  const isLight = tooltipStyle === 'light';

  const titleLabel = t('chargePoint.tooltipTitle', { count: totalCount });

  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none z-50">
      <div
        className={`px-3 py-2 rounded-lg shadow-monta-lg whitespace-nowrap ${
          isLight
            ? 'bg-white text-gray-900 border border-gray-200'
            : 'bg-monta-black text-white border border-gray-700'
        }`}
      >
        {/* Total count */}
        <div
          className={`font-bold text-sm mb-1.5 pb-1.5 ${
            isLight ? 'border-b border-gray-200' : 'border-b border-gray-700'
          }`}
        >
          {titleLabel}
        </div>
        {/* State breakdown */}
        <div className="space-y-1">
          {states.map((state, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-3 text-xs"
            >
              <span className="font-semibold" style={{ color: state.color }}>
                {state.count}
              </span>
              <span className={isLight ? 'text-gray-600' : 'text-gray-300'}>
                {state.label}
              </span>
            </div>
          ))}
        </div>
        {/* Arrow pointing down */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
          <div
            className={`w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] ${
              isLight ? 'border-t-gray-200' : 'border-t-gray-700'
            }`}
            style={{ filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.1))' }}
          />
        </div>
      </div>
    </div>
  );
}
